'use client';

import type {FC} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {MdOutlineLocalPhone} from 'react-icons/md';
import clsx from 'clsx';
import Image from 'next/image';

import {useEventListener} from '@/modules/foundation/hooks/useEventListener';
import {createPhoneLink, formatPhoneNumber} from '@/modules/foundation/shared/format';
import {GAUS_PHONE} from '@/shared/constants';

import ekspert from '../../../public/images/ekspert.jpg';

import {Hamburger} from './Hamburger';
import type {NavigationType} from './Navbar';
import {NavLink} from './NavLink';

const MENU_STRING = 'Meny';

type Props = {
	navigation: NavigationType;
};

export const MobileNav: FC<Props> = ({navigation}) => {
	const listRef = useRef<HTMLUListElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const navRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	/**
	 * If user click outside the navigation,
	 * close the mobile navigation
	 */
	const handleOutsideClick = (event: MouseEvent) => {
		if (!isOpen) return;

		if (!navRef.current?.contains(event.target as Node)) {
			setIsOpen(false);
		}
	};
	useEventListener('click', handleOutsideClick);

	/**
	 * If user press Escape key, close the mobile navigation
	 * and set focus on the trigger
	 */
	const handleEscape = (event: KeyboardEvent) => {
		if (!isOpen) return;

		if (event.key === 'Escape') {
			setIsOpen(false);
			triggerRef.current?.focus();
		}
	};
	useEventListener('keydown', handleEscape);

	/**
	 * On first focus inside the navRef,
	 * set focusInsideNav to true
	 */
	const handleFocus = (event: FocusEvent) => {
		const insideNav = navRef.current?.contains(event.target as Node);
		if (!insideNav && isOpen) {
			setIsOpen(false);
		}
	};
	useEventListener('focusin', handleFocus);

	/**
	 * If user opens the mobile navigation, scroll the listRef
	 * to the top
	 */
	useEffect(() => {
		if (isOpen) {
			listRef.current?.scrollTo(0, 0);
		}
	}, [isOpen]);

	const handleClick = useCallback(() => {
		if (isOpen) {
			setIsOpen(false);
		} else {
			setIsOpen(true);
		}
	}, [isOpen]);

	return (
		<nav className={clsx('md:hidden')} ref={navRef}>
			{/**
			 * The clickarea should be at least 48x48px for accessibility
			 */}
			<div className={clsx('flex', 'justify-between')}>
				<button
					ref={triggerRef}
					onClick={handleClick}
					aria-expanded={isOpen}
					className={clsx(
						'w-12',
						'h-12',
						'flex',
						'justify-center',
						'items-center',
						'relative',
						'-left-3',
					)}
				>
					<span className={clsx('sr-only')}>{MENU_STRING}</span>
					<Hamburger isOpen={isOpen} />
				</button>
				<div className="flex flex-col gap-0">
					<span className={clsx('flex', 'items-center', 'gap-1', 'font-bold')}>
						<MdOutlineLocalPhone aria-hidden />
						<span className="sr-only">Telefon</span>
						<a href={createPhoneLink(GAUS_PHONE)}>{formatPhoneNumber(GAUS_PHONE)}</a>
					</span>
					<span className="text-xs leading-tight">08-16, hverdager</span>
				</div>
			</div>

			<div
				className={clsx(
					isOpen ? 'flex' : 'hidden',
					'flex-col',
					'fixed', // fixed positioning relative to viewport
					'inset-0', // sets top, right, bottom, and left to 0
					'bg-white',
					'overflow-y-auto',
					'p-6',
					'z-50',
					'gap-6',
					'animate-expand',
					'border-l-8', // sets left border width to 8px
					'border-l-red-500', // sets left border color to red
				)}
			>
				<button
					ref={triggerRef}
					onClick={handleClick}
					aria-expanded={isOpen}
					className={clsx('w-12', 'h-12', 'first:self-end')}
				>
					<span className={clsx('sr-only')}>{MENU_STRING}</span>
					<Hamburger isOpen={isOpen} />
				</button>
				<ul ref={listRef}>
					{navigation.map((item) => {
						if (item.type === 'navlink') {
							return (
								<li
									key={item._key}
									className={clsx('[&_*]:font-bold', '[&_*]:text-[16px]', 'pb-5')}
								>
									<NavLink link={item} onClick={handleClick} />
								</li>
							);
						}
					})}
				</ul>
				<div
					className={clsx('flex', 'items-center', 'gap-3', 'relative', 'py-4', 'mt-auto')}
				>
					<Image
						src={ekspert}
						alt=""
						height={40}
						width={40}
						className={clsx('rounded-full')}
					/>
					<div>
						<span
							className={clsx(
								'flex',
								'items-center',
								'gap-1',
								'text-primary',
								'font-bold',
							)}
						>
							<MdOutlineLocalPhone aria-hidden />
							<span className="sr-only">Telefon </span>
							<a
								href={createPhoneLink(GAUS_PHONE)}
								className={clsx('after:absolute', 'after:inset-0')}
							>
								{formatPhoneNumber(GAUS_PHONE)}
							</a>
						</span>
						<span className={clsx('text-beige-500')}>08-16, hverdager</span>
					</div>
				</div>
			</div>
		</nav>
	);
};
